// Private dashboard: which contact CTAs actually get clicked.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ADMIN_TOKEN_KEY = "cta-clicks:admin-token:v1";

function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter ADMIN_TOKEN to view CTA analytics") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  return token;
}

interface Tally {
  name: string;
  count: number;
}
interface Payload {
  days: number;
  total: number;
  byChannel: Tally[];
  byPlacement: Tally[];
  byPage: Tally[];
  daily: Array<Record<string, string | number>>;
  recent: Array<{
    channel: string;
    placement: string;
    path: string | null;
    created_at: string;
  }>;
}

export const Route = createFileRoute("/admin/cta-clicks")({
  head: () => ({
    meta: [
      { title: "CTA Click Analytics | Arif Khan" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "Private CTA conversion dashboard." },
    ],
  }),
  component: CtaClicksDashboard,
});

const CHANNELS = ["phone", "whatsapp", "email", "linkedin", "calendar", "form"];
const COLORS: Record<string, string> = {
  phone: "#0ea5e9",
  whatsapp: "#25D366",
  email: "#f59e0b",
  linkedin: "#2563eb",
  calendar: "#a78bfa",
  form: "#22d3ee",
};

function CtaClicksDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/cta-clicks?days=${d}`, {
        headers: { "x-admin-token": getAdminToken() },
        cache: "no-store",
      });
      if (!res.ok) {
        if (res.status === 401)
          window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        throw new Error(`Request failed (${res.status})`);
      }
      setData((await res.json()) as Payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              CTA Click Analytics
            </h1>
            <p className="mt-2 text-sm text-ink-soft">
              First-party tracking of phone, WhatsApp, email and other contact
              buttons across the site.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={d === days ? "default" : "outline"}
                size="sm"
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => void load(days)}>
              Refresh
            </Button>
          </div>
        </header>

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}
        {loading && !data && <p className="text-sm text-ink-soft">Loading…</p>}

        {data && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-ink-soft">
                    Total clicks
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-3xl font-semibold">
                  {data.total}
                </CardContent>
              </Card>
              {data.byChannel.slice(0, 2).map((c) => (
                <Card key={c.name}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-ink-soft capitalize">
                      Top channel — {c.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-3xl font-semibold">
                    {c.count}
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Clicks over time</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Legend />
                    {CHANNELS.map((c) => (
                      <Line
                        key={c}
                        type="monotone"
                        dataKey={c}
                        stroke={COLORS[c]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>By channel</CardTitle>
                </CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.byChannel}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis allowDecimals={false} fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="count" fill="var(--color-primary)" radius={4} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>By placement</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {data.byPlacement.length === 0 && (
                    <p className="text-ink-soft">No clicks recorded yet.</p>
                  )}
                  {data.byPlacement.slice(0, 14).map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between gap-4 border-b border-border/60 pb-1"
                    >
                      <span className="truncate">{p.name}</span>
                      <span className="font-semibold">{p.count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Top pages driving clicks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.byPage.length === 0 && (
                  <p className="text-ink-soft">No data yet.</p>
                )}
                {data.byPage.map((p) => (
                  <div
                    key={p.name}
                    className="flex items-center justify-between gap-4 border-b border-border/60 pb-1"
                  >
                    <span className="truncate">{p.name}</span>
                    <span className="font-semibold">{p.count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
